import { useEffect, useState } from 'react';
import api from '@/lib/api';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Calendar, Users, UsersRound, Filter, RotateCcw, Megaphone, Radio } from 'lucide-react';
import {
  vendorsVisibleForGroup,
  groupsVisibleForVendor,
  campaignsVisibleForGroup,
  groupsVisibleForCampaign,
  reconcileFilters,
  byFacet,
} from '@/lib/filterCascade';

/**
 * Filtros compartidos (Dashboard + Conversaciones IA).
 * Tipo Excel: cada filtro ACUMULA. Grupo y vendedor se acotan entre sí.
 */

const _localDateStr = (d) =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

const defaultDateFrom = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
};
const defaultDateTo = () => _localDateStr(new Date());

export const DEFAULT_FILTERS = Object.freeze({
  date_from: defaultDateFrom(),
  date_to: defaultDateTo(),
  seller_id: 'all',
  group_id: 'all',
  campaign_id: 'all',
  lead_source_id: 'all',
});

export default function DashboardFilters({ value, onChange, hide = [], testid = 'dashboard-filters' }) {
  const filters = { ...DEFAULT_FILTERS, ...value };
  const [vendors, setVendors] = useState([]);
  const [groups, setGroups] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [leadSources, setLeadSources] = useState([]);
  const [facets, setFacets] = useState(null);

  useEffect(() => {
    api.get('/team').then(r => {
      const team = Array.isArray(r.data) ? r.data : [];
      setVendors(team.filter(m => m.role !== 'superadmin'));
    }).catch(() => setVendors([]));

    api.get('/roulette/groups').then(r => {
      const all = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setGroups(all.filter(g => g.is_active !== false));
    }).catch(() => setGroups([]));

    api.get('/campaigns', { params: { include_hidden: true } }).then(r => {
      setCampaigns(Array.isArray(r.data) ? r.data : []);
    }).catch(() => setCampaigns([]));

    api.get('/lead-sources').then(r => {
      setLeadSources(Array.isArray(r.data) ? r.data : (r.data?.items || []));
    }).catch(() => setLeadSources([]));
  }, []);

  useEffect(() => {
    const p = filtersToParams(filters);
    if (p.date_from && p.date_to) p.date_preset = 'custom';
    if (p.seller_id) p.assigned_to = p.seller_id;
    let cancelled = false;
    api.get('/leads/filter-facets', { params: p }).then(r => {
      if (!cancelled) setFacets(r.data || null);
    }).catch(() => {
      if (!cancelled) setFacets(null);
    });
    return () => { cancelled = true; };
  }, [
    filters.date_from, filters.date_to, filters.seller_id, filters.group_id,
    filters.campaign_id, filters.lead_source_id,
  ]);

  const visibleVendors = byFacet(
    vendorsVisibleForGroup(vendors, groups, filters.group_id),
    facets?.assigned_to,
    filters.seller_id,
  );
  const visibleGroups = byFacet(
    groupsVisibleForCampaign(
      groupsVisibleForVendor(groups, filters.seller_id),
      campaigns,
      filters.campaign_id,
    ),
    facets?.group_id,
    filters.group_id,
  );
  const visibleCampaigns = byFacet(
    campaignsVisibleForGroup(campaigns, filters.group_id),
    facets?.campaign_id,
    filters.campaign_id,
  );
  const visibleSources = byFacet(leadSources, facets?.lead_source_id, filters.lead_source_id);

  const update = (patch) => {
    if (typeof onChange !== 'function') return;
    onChange(reconcileFilters(filters, groups, campaigns, patch));
  };

  const reset = () => {
    if (typeof onChange === 'function') {
      onChange({ ...DEFAULT_FILTERS });
    }
  };

  const showDate = !hide.includes('date');
  const showSeller = !hide.includes('seller');
  const showGroup = !hide.includes('group');
  const showCampaign = !hide.includes('campaign');
  const showSource = !hide.includes('source');

  return (
    <div className="flex flex-wrap items-center gap-2 p-3 rounded-lg bg-card/40 backdrop-blur-md border border-white/5 mb-6" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <Filter className="w-3.5 h-3.5" />
        <span>Filtros</span>
        <span className="text-[10px] text-muted-foreground/80 hidden sm:inline">se acumulan</span>
      </div>

      {showDate && (
        <div className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-black/20 border border-white/5">
          <Calendar className="w-3 h-3 text-muted-foreground" />
          <Input
            type="date"
            value={filters.date_from || ''}
            onChange={e => update({ date_from: e.target.value })}
            className="h-6 w-32 bg-transparent border-0 text-[11px] p-0 focus:ring-0"
            data-testid={`${testid}-date-from`}
          />
          <span className="text-[10px] text-muted-foreground">→</span>
          <Input
            type="date"
            value={filters.date_to || ''}
            onChange={e => update({ date_to: e.target.value })}
            className="h-6 w-32 bg-transparent border-0 text-[11px] p-0 focus:ring-0"
            data-testid={`${testid}-date-to`}
          />
        </div>
      )}

      {showSeller && (
        <div className="flex items-center gap-1 min-w-[180px]">
          <Users className="w-3.5 h-3.5 text-muted-foreground" />
          <Select
            value={filters.seller_id || 'all'}
            onValueChange={v => update({ seller_id: v })}
          >
            <SelectTrigger className="h-7 bg-black/20 border-white/10 text-[11px]" data-testid={`${testid}-seller`}>
              <SelectValue placeholder="Todos los vendedores" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                {filters.group_id && filters.group_id !== 'all' ? 'Todos los del grupo' : 'Todos los vendedores'}
              </SelectItem>
              {visibleVendors.map(v => (
                <SelectItem key={v.id} value={v.id} className="text-xs">{v.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showGroup && (
        <div className="flex items-center gap-1 min-w-[180px]">
          <UsersRound className="w-3.5 h-3.5 text-muted-foreground" />
          <Select
            value={filters.group_id || 'all'}
            onValueChange={v => update({ group_id: v })}
          >
            <SelectTrigger className="h-7 bg-black/20 border-white/10 text-[11px]" data-testid={`${testid}-group`}>
              <SelectValue placeholder="Todos los grupos" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                {filters.seller_id && filters.seller_id !== 'all' ? 'Todos los grupos de este vendedor' : 'Todos los grupos'}
              </SelectItem>
              {visibleGroups.map(g => (
                <SelectItem key={g.id} value={g.id} className="text-xs">{g.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showCampaign && (
        <div className="flex items-center gap-1 min-w-[180px]">
          <Megaphone className="w-3.5 h-3.5 text-muted-foreground" />
          <Select
            value={filters.campaign_id || 'all'}
            onValueChange={v => update({ campaign_id: v })}
          >
            <SelectTrigger className="h-7 bg-black/20 border-white/10 text-[11px]" data-testid={`${testid}-campaign`}>
              <SelectValue placeholder="Todas las campañas" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">
                {filters.group_id && filters.group_id !== 'all' ? 'Todas las del grupo' : 'Todas las campañas'}
              </SelectItem>
              {visibleCampaigns.map(c => (
                <SelectItem key={c.id} value={c.id} className="text-xs">{c.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {showSource && (
        <div className="flex items-center gap-1 min-w-[180px]">
          <Radio className="w-3.5 h-3.5 text-muted-foreground" />
          <Select
            value={filters.lead_source_id || 'all'}
            onValueChange={v => update({ lead_source_id: v })}
          >
            <SelectTrigger className="h-7 bg-black/20 border-white/10 text-[11px]" data-testid={`${testid}-source`}>
              <SelectValue placeholder="Todas las fuentes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" className="text-xs">Todas las fuentes</SelectItem>
              {visibleSources.map(s => (
                <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      <Button
        variant="ghost"
        size="sm"
        onClick={reset}
        className="h-7 ml-auto text-muted-foreground hover:text-white"
        data-testid={`${testid}-reset`}
      >
        <RotateCcw className="w-3 h-3 mr-1" />
        <span className="text-[10px]">Reset</span>
      </Button>
    </div>
  );
}

export function filtersToParams(filters) {
  const params = {};
  if (filters?.date_from) params.date_from = filters.date_from;
  if (filters?.date_to) params.date_to = filters.date_to;
  if (filters?.seller_id && filters.seller_id !== 'all') params.seller_id = filters.seller_id;
  if (filters?.group_id && filters.group_id !== 'all') params.group_id = filters.group_id;
  if (filters?.campaign_id && filters.campaign_id !== 'all') params.campaign_id = filters.campaign_id;
  if (filters?.lead_source_id && filters.lead_source_id !== 'all') params.lead_source_id = filters.lead_source_id;
  return params;
}
